import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaPurpose } from '@up2d8/contracts';
import { useSession } from './session';
import { safeNavigationUrl } from './safe-url';

export interface UploadState {
  /** 0–100 tijdens het uploaden, `null` als er niets loopt. */
  progress: number | null;
  fileName: string | null;
  error: string | null;
}

/**
 * Een bestand uploaden, in drie stappen zoals de API ze bedoelt: plek vragen, bytes naar de
 * opslag, afronden.
 *
 * `XMLHttpRequest` in plaats van `fetch` voor de tweede stap, en dat is geen nostalgie:
 * `fetch` geeft geen voortgang van een upload. Op een fabrieksnetwerk duurt een foto van tien
 * megabyte een halve minuut, en dan is een balk het verschil tussen wachten en nog een keer
 * drukken.
 *
 * De bytes gaan rechtstreeks naar de opslag, dus zonder onze `Authorization`-header. De
 * handtekening in de URL is de toegang; een header erbij zou de opslag juist doen weigeren.
 */
export function useUpload() {
  const { api } = useSession();
  const { t } = useTranslation();
  const [state, setState] = useState<UploadState>({
    progress: null,
    fileName: null,
    error: null,
  });
  const request = useRef<XMLHttpRequest | null>(null);

  const cancel = useCallback(() => {
    request.current?.abort();
    request.current = null;
    setState({ progress: null, fileName: null, error: null });
  }, []);

  const upload = useCallback(
    async (
      file: File,
      options: {
        ownerModule: string;
        purpose: MediaPurpose;
        /** Alleen een platformbeheerder geeft dit mee; hij heeft geen bedrijf in zijn sessie. */
        tenantId?: string;
      },
    ): Promise<string | null> => {
      setState({ progress: 0, fileName: file.name, error: null });

      try {
        const ticket = await api.POST('/api/v1/media/uploads', {
          body: {
            ownerModule: options.ownerModule,
            purpose: options.purpose,
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            ...(options.tenantId ? { tenantId: options.tenantId } : {}),
          },
        });
        if (ticket.error || !ticket.data) {
          // De server zegt in gewone taal wat er mis is: te groot, verkeerd type. Die zin is
          // beter dan een eigen vertaling ernaast, die zou gaan afwijken.
          const message =
            (ticket.error as { errors?: { message: string }[] } | undefined)?.errors?.[0]
              ?.message ?? t('errors.generic');
          setState({ progress: null, fileName: null, error: message });
          return null;
        }

        await put(ticket.data.uploadUrl, ticket.data.headers, file, (progress) =>
          setState((current) => ({ ...current, progress })),
        );

        const completed = await api.POST('/api/v1/media/{fileId}/complete', {
          params: { path: { fileId: ticket.data.fileId } },
        });
        if (completed.error || !completed.data) {
          setState({ progress: null, fileName: null, error: t('errors.generic') });
          return null;
        }

        // Afgerond is niet hetzelfde als goedgekeurd: de server controleert pas hier of de
        // inhoud bij het type past, en zegt dan waarom niet.
        if (completed.data.status !== 'ready') {
          setState({
            progress: null,
            fileName: null,
            error: completed.data.rejectedReason ?? t('media.rejected'),
          });
          return null;
        }

        setState({ progress: null, fileName: null, error: null });
        return completed.data.id;
      } catch (error) {
        const aborted = error instanceof DOMException && error.name === 'AbortError';
        setState({
          progress: null,
          fileName: null,
          error: aborted
            ? null
            : error instanceof StorageUnreachable
              ? t('media.storageUnreachable')
              : t('media.uploadFailed'),
        });
        return null;
      } finally {
        request.current = null;
      }
    },
    [api, t],
  );

  /** De PUT naar de opslag, met voortgang en met een knop om te stoppen. */
  function put(
    url: string,
    headers: Record<string, string>,
    file: File,
    onProgress: (progress: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      request.current = xhr;
      xhr.open('PUT', url);
      for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`opslag gaf ${xhr.status}`));
      });
      xhr.addEventListener('error', () => reject(new StorageUnreachable()));
      xhr.addEventListener('abort', () => reject(new DOMException('afgebroken', 'AbortError')));
      xhr.send(file);
    });
  }

  return { ...state, upload, cancel };
}

/**
 * De URL waarmee een `<img>` een bestand laat zien.
 *
 * Kortlevend (zestig seconden), dus hij hoort niet in een cache die langer meegaat dan het
 * scherm. Daarom wordt hij per weergave opgehaald en niet bij het bestand bewaard.
 */
export function useFileUrl() {
  const { api } = useSession();

  return useCallback(
    async (fileId: string, variant: 'original' | 'thumbnail' = 'original') => {
      const { data, error } = await api.GET('/api/v1/media/{fileId}/download', {
        params: { path: { fileId }, query: { variant } },
      });
      if (error || !data) return null;
      return safeNavigationUrl(data.url);
    },
    [api],
  );
}

/**
 * De browser kreeg de opslag niet te pakken: geen antwoord, geblokkeerd (CORS, mixed content) of
 * een adres dat niet bestaat.
 *
 * Een eigen fout omdat de beheerder hier niets aan kan doen, en dat moet hij weten. Op staging
 * bleef elke upload hangen omdat de URL naar het interne adres van de opslag wees, en "Probeer
 * het opnieuw" deed vermoeden dat het aan het bestand lag.
 */
class StorageUnreachable extends Error {
  constructor() {
    super('opslag niet bereikbaar');
  }
}
